import { isNil } from 'lodash';
import React, { useCallback, useContext } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import {
  TimeTrackerButton,
  TimerText,
  calculateDurationFromTimeTrackerData,
  isTimerRunning,
} from '@shared/components/timer';
import { MBTextBody1 } from '@shared/components/typography';
import {
  TimeTrackerComponentData,
  TimeTrackerField,
  isTimeTrackerComponentData,
} from '@shared/data/entity/fields/TimeTrackerField';
import { useUpdateWorkflowEntity } from '@shared/scenes/entity/edit/hooks/useUpdateWorkflowEntity';
import { ColorThemeContext, grid } from '@shared/styles';
import { useBoolean } from '@shared/util/hooks';
import { CardCollectionViewFieldContainer } from './CardCollectionViewFieldContainer';

//#region Public Interface

interface Props {
  component: MBAPI.WorkflowTemplate_components_TimeTrackerComponent;
  entityId: string;
  field: TimeTrackerField;
  canUpdateCard: boolean;
  showIcon?: boolean;
  style: StyleProp<ViewStyle>;
}

export function TimeTrackerCollectionViewField({
  component,
  entityId,
  field,
  canUpdateCard,
  showIcon,
  style,
}: Props) {
  const colors = useContext(ColorThemeContext);

  const data = field.timerEntries;
  if (isNil(data) || !isTimeTrackerComponentData(data)) {
    return null;
  }

  const isRunning = isTimerRunning(data);

  return (
    <CardCollectionViewFieldContainer
      style={[
        {
          paddingVertical: grid(0.5),
          alignItems: 'flex-start',
        },
        style,
      ]}
    >
      <View style={{ alignItems: 'stretch' }}>
        <View
          row
          centerVertically
          paddingLeft={showIcon ? 0.1 : 0}
          paddingRight={showIcon ? 1.25 : 0}
          style={{ justifyContent: 'flex-start' }}
        >
          {showIcon && <MBIconV2 name="time-stopwatch" size={28} color={colors.accent} />}
          <MBTextBody1 style={{ marginStart: showIcon ? grid(0.5) : 0 }}>
            <TimerText
              isRunning={isRunning}
              onTick={(now: Date) => calculateDurationFromTimeTrackerData(data, now)}
            />
          </MBTextBody1>
        </View>
        {!component.readonly && canUpdateCard && (
          <FieldUpdatingTimeTrackerButton
            entityId={entityId}
            component={component}
            data={data}
            isRunning={isRunning}
            style={{
              marginHorizontal: showIcon ? 0 : -grid(1.25),
              marginTop: grid(1),
            }}
          />
        )}
      </View>
    </CardCollectionViewFieldContainer>
  );
}

//#endregion

//#region Private Interface

function FieldUpdatingTimeTrackerButton({
  entityId,
  component,
  data,
  isRunning,
  style,
}: {
  entityId: string;
  component: MBAPI.WorkflowTemplate_components_TimeTrackerComponent;
  data: TimeTrackerComponentData;
  isRunning: boolean;
  style: StyleProp<ViewStyle>;
}) {
  const { updateWorkflowEntity } = useUpdateWorkflowEntity();
  const [loading, startLoading, stopLoading] = useBoolean(false);

  const handleDataChanged = useCallback(
    async (newData: TimeTrackerComponentData) => {
      startLoading();
      await updateWorkflowEntity(entityId, {
        [component.id]: newData,
      });
      stopLoading();
    },
    [entityId, component.id, updateWorkflowEntity, startLoading, stopLoading],
  );

  return (
    <TimeTrackerButton
      component={component}
      data={data}
      loading={loading}
      isRunning={isRunning}
      size="small"
      style={style}
      onDataChanged={handleDataChanged}
    />
  );
}

//#endregion
